import { useCallback, useEffect, useRef, useState } from 'react';

//: how long the focus ring stays after a tap (its fade is in FocusRing.js)
export const RING_MS = 1200;

/**
 * Tap to focus and expose, with the ring that answers it (2026-10-20): every
 * live camera hands its picture's taps to `onTap`, which asks the camera to
 * focus and meter the light there (useCameraControls.focusAt) and puts a ring
 * where the finger was -- {id, left, top, status} in px from `areaRef`'s
 * top-left (else the tapped element's) -- for FocusRing.js to draw.
 *
 * status: 'pending' while the camera is asked, then 'ok' or 'refused' by its
 * answer; 'auto' where the camera takes no point (an iPhone's focuses by
 * itself). The ring goes after RING_MS; a new tap moves it at once.
 */
export default function useTapFocus(controls, { fit = 'cover', areaRef = null } = {}) {
  const { focusAt } = controls;
  const [ring, setRing] = useState(null);
  const timer = useRef(null);
  const seq = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onTap = useCallback((event) => {
    const hit = focusAt(event, fit);
    if (!hit) return;
    const area = areaRef?.current || event.currentTarget;
    const rect = area && typeof area.getBoundingClientRect === 'function' ? area.getBoundingClientRect() : null;
    if (!rect) return;
    seq.current += 1;
    const id = seq.current;
    setRing({ id, left: event.clientX - rect.left, top: event.clientY - rect.top, status: hit.supported ? 'pending' : 'auto' });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setRing(r => (r && r.id === id ? null : r)), RING_MS);
    if (hit.supported) {
      Promise.resolve(hit.done).then((ok) => {
        setRing(r => (r && r.id === id ? { ...r, status: ok ? 'ok' : 'refused' } : r));
      });
    }
  }, [focusAt, fit, areaRef]);

  return { ring, onTap };
}
